import { useState, useEffect, useRef } from 'react';

import { Box, CircularProgress, Typography } from '@mui/material';

import { OutlineButton } from 'components/buttons/OutlineButton';

const RoamSsidTable = ({ isLoading, ssidStatsList }) => {
  const [ssidVisibleCount, setSsidVisibleCount] = useState(5);
  const ssidScrollRef = useRef(null);
  const ssidScrollTrackRef = useRef(null);
  const ssidDragRef = useRef(false);
  const [ssidScrollBar, setSsidScrollBar] = useState({
    show: false,
    thumbWidthPct: 0,
    thumbLeftPct: 0,
  });

  const updateSsidScrollBar = () => {
    const el = ssidScrollRef.current;
    if (!el) return;

    const { scrollWidth, clientWidth, scrollLeft } = el;
    if (!scrollWidth || scrollWidth <= clientWidth) {
      setSsidScrollBar({ show: false, thumbWidthPct: 0, thumbLeftPct: 0 });
      return;
    }

    const thumbWidthPct = 35;
    const maxLeftPct = 100 - thumbWidthPct;
    const maxScrollLeft = scrollWidth - clientWidth;
    const thumbLeftPct = maxScrollLeft > 0 ? (scrollLeft / maxScrollLeft) * maxLeftPct : 0;

    setSsidScrollBar({ show: true, thumbWidthPct, thumbLeftPct });
  };

  useEffect(() => {
    updateSsidScrollBar();
  }, [ssidStatsList.length, ssidVisibleCount]);

  useEffect(() => {
    const el = ssidScrollRef.current;
    if (!el) return;

    const onScroll = () => updateSsidScrollBar();
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', updateSsidScrollBar);
    updateSsidScrollBar();

    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', updateSsidScrollBar);
    };
  }, [isLoading, ssidStatsList.length]);

  const updateSsidScrollByClientX = clientX => {
    const el = ssidScrollRef.current;
    const trackEl = ssidScrollTrackRef.current;
    if (!el || !trackEl) return;

    const rect = trackEl.getBoundingClientRect();
    if (!rect.width) return;

    const positionPct = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1);
    const maxScrollLeft = el.scrollWidth - el.clientWidth;
    el.scrollLeft = positionPct * maxScrollLeft;
  };

  useEffect(() => {
    const handleMouseMove = event => {
      if (!ssidDragRef.current) return;
      updateSsidScrollByClientX(event.clientX);
    };

    const handleMouseUp = () => {
      ssidDragRef.current = false;
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <Box sx={{ width: '100%' }}>
      {isLoading ? (
        <Box
          sx={{
            height: 220,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : ssidStatsList.length === 0 ? (
        <Typography sx={{ color: '#8E8E93', textAlign: 'center', py: 4 }}>No data available</Typography>
      ) : (
        <>
          <Box
            ref={ssidScrollRef}
            sx={{
              width: '100%',
              overflowX: 'auto',
              overflowY: 'hidden',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': {
                display: 'none',
              },
            }}
          >
            <Box sx={{ minWidth: 'max-content' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  color: '#172664',
                  fontSize: 15,
                  fontWeight: 500,
                  pb: 1.5,
                  borderBottom: '1px solid #E5E5EA',
                }}
              >
                <Box sx={{ width: 30, flex: '0 0 auto' }}>#</Box>
                <Box sx={{ width: 220, flex: '0 0 auto' }}>SSID</Box>
                <Box sx={{ width: 120, flex: '0 0 auto' }}>Roam Events</Box>
                <Box sx={{ width: 120, flex: '0 0 auto' }}>Success Rate</Box>
                <Box sx={{ width: 220, flex: '0 0 auto' }}>95% Roams Complete Within</Box>
              </Box>

              {ssidStatsList.slice(0, ssidVisibleCount).map(row => (
                <Box
                  key={`${row.index}-${row.apPairFromTo}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    py: 2,
                    borderBottom: '1px solid #F2F2F7',
                    color: '#1C1C1E',
                    fontSize: 14,
                    fontWeight: 400,
                  }}
                >
                  <Box sx={{ width: 30, flex: '0 0 auto', color: '#1C1C1E' }}>{row.index}</Box>
                  <Box
                    sx={{
                      width: 220,
                      flex: '0 0 auto',
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                    }}
                  >
                    {row.apPairFromTo}
                  </Box>
                  <Box sx={{ width: 120, flex: '0 0 auto' }}>{row.roamEvents}</Box>
                  <Box sx={{ width: 120, flex: '0 0 auto' }}>{row.successRate}</Box>
                  <Box sx={{ width: 220, flex: '0 0 auto' }}>{row.p95Latency}</Box>
                </Box>
              ))}
            </Box>
          </Box>
          {ssidScrollBar.show && (
            <Box sx={{ mt: 1.5, px: 1 }}>
              <Box
                ref={ssidScrollTrackRef}
                onMouseDown={event => {
                  ssidDragRef.current = true;
                  updateSsidScrollByClientX(event.clientX);
                }}
                sx={{
                  position: 'relative',
                  height: 5.5,
                  borderRadius: 999,
                  backgroundColor: '#D1D1D6',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: `${ssidScrollBar.thumbLeftPct}%`,
                    width: `${ssidScrollBar.thumbWidthPct}%`,
                    borderRadius: 999,
                    backgroundColor: '#172664',
                    cursor: 'grab',
                  }}
                />
              </Box>
            </Box>
          )}

          {ssidStatsList.length > ssidVisibleCount && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <OutlineButton
                variant="text"
                onClick={() => setSsidVisibleCount(v => Math.min(v + 5, ssidStatsList.length))}
                sx={{
                  height: 32,
                  textTransform: 'none',
                  color: '#172664',
                  border: '1px solid #172664',
                  backgroundColor: 'transparent',
                }}
              >
                Show more
              </OutlineButton>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default RoamSsidTable;
